import React from 'react';
import Axios from 'axios';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getAuthHeader } from '../../utils/authToken';

export const answerLines = value => (Array.isArray(value) ? value : [])
    .map(item => String(item))
    .join('\n');

export const parseAnswerLines = value => String(value || '')
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);

export const issueState = issue => issue.latest_decision?.status || 'open';

export const textbookDisplay = question => question?.textbook_title
    || `Unknown textbook (ID ${question?.textbook_id ?? '—'})`;

const formatValue = value => {
    if (Array.isArray(value)) return value.join('\n');
    if (value == null) return '—';
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
};

const stateLabel = state => ({
    key_preview: 'Previewed',
    key_applied: 'Applied',
    key_rejected: 'Rejected',
    open: 'Open',
}[state] || state);

const stateColor = state => ({
    key_preview: 'info',
    key_applied: 'success',
    key_rejected: 'default',
    open: 'warning',
}[state] || 'default');

const actionColor = action => ({
    upgrade: 'success',
    protected_downgrade: 'warning',
    blocked_downgrade: 'error',
    review_required: 'info',
    ungradable: 'warning',
}[action] || 'default');

const PreviewDialog = ({ preview, open, applying, onClose, onRequestApply }) => (
    <Dialog open={open} onClose={applying ? undefined : onClose} fullWidth maxWidth="lg">
        <DialogTitle>Key correction preview · Question {preview?.question_id}</DialogTitle>
        <DialogContent>
            {preview && (
                <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Applying changes the answer key and upgrades only deterministic Incorrect → Correct
                        results. Correct → Incorrect changes and protected grades remain unchanged.
                    </Alert>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                            <Typography variant="caption" fontWeight="bold">Current key</Typography>
                            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                {formatValue(preview.current_answer)}
                            </Box>
                        </Paper>
                        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
                            <Typography variant="caption" fontWeight="bold">Proposed key</Typography>
                            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                {formatValue(preview.proposed_answer)}
                            </Box>
                        </Paper>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        <Chip label={`${preview.counts.total} affected`} />
                        <Chip color="success" label={`${preview.counts.upgrade} upgrades`} />
                        <Chip color="warning" label={`${preview.counts.protected_downgrade} protected downgrades`} />
                        <Chip color="error" label={`${preview.counts.blocked_downgrade} blocked downgrades`} />
                        <Chip color="info" label={`${preview.counts.review_required} need review`} />
                    </Stack>
                    {(preview.counts.protected_downgrade > 0 || preview.counts.blocked_downgrade > 0) && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Downgrades are visible for review but cannot be applied by this workflow.
                        </Alert>
                    )}
                    <Typography variant="h6" sx={{ mb: 1 }}>Full proposed-change list</Typography>
                    {preview.items.length === 0 && (
                        <Typography color="text.secondary">No existing answers use this question.</Typography>
                    )}
                    {preview.items.map(item => (
                        <Paper key={item.hw_answer_id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                                <Box sx={{ minWidth: 180 }}>
                                    <Typography fontWeight="bold">Answer #{item.hw_answer_id}</Typography>
                                    <Typography variant="caption">Submission #{item.hw_submission_id}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption">Student answer</Typography>
                                    <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                        {formatValue(item.student_answer)}
                                    </Box>
                                </Box>
                                <Typography variant="body2">
                                    {String(item.current_is_correct)} → {String(item.proposed_is_correct)}
                                </Typography>
                                <Chip size="small" color={actionColor(item.action)} label={item.action} />
                            </Stack>
                        </Paper>
                    ))}
                </>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} disabled={applying}>Close</Button>
            <Button variant="contained" color="success" onClick={onRequestApply} disabled={applying || !preview}>
                Review and apply upgrades
            </Button>
        </DialogActions>
    </Dialog>
);

const KeyRemediationReview = () => {
    const [queue, setQueue] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    const [notice, setNotice] = React.useState('');
    const [filter, setFilter] = React.useState('open');
    const [editing, setEditing] = React.useState(null);
    const [proposedText, setProposedText] = React.useState('');
    const [preview, setPreview] = React.useState(null);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [confirmed, setConfirmed] = React.useState(false);
    const [rejecting, setRejecting] = React.useState(null);
    const [rejectionNote, setRejectionNote] = React.useState('');

    const headers = React.useMemo(() => ({ Authorization: getAuthHeader() }), []);

    const loadQueue = React.useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/hw-key-remediations`, { headers });
            setQueue(response.data.items || []);
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Failed to load key-remediation queue.');
        } finally {
            setLoading(false);
        }
    }, [headers]);

    React.useEffect(() => { loadQueue(); }, [loadQueue]);

    const openEditor = issue => {
        setEditing(issue);
        setProposedText(answerLines(issue.latest_decision?.proposed_answer || issue.proposed_answer));
        setError('');
    };

    const createPreview = async () => {
        const proposedAnswer = parseAnswerLines(proposedText);
        if (proposedAnswer.length === 0) {
            setError('Enter at least one proposed answer-key value.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-key-remediations/${editing.question_id}/preview`,
                { proposed_answer: proposedAnswer },
                { headers },
            );
            setPreview(response.data);
            setEditing(null);
            await loadQueue();
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Failed to create remediation preview.');
        } finally {
            setBusy(false);
        }
    };

    const applyPreview = async () => {
        setBusy(true);
        setError('');
        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-key-remediation-batches/${preview.batch_id}/apply`,
                { input_hash: preview.input_hash },
                { headers },
            );
            setNotice(`Key updated. ${response.data.applied_count} Incorrect → Correct grade(s) applied.`);
            setConfirmOpen(false);
            setConfirmed(false);
            setPreview(null);
            await loadQueue();
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Failed to apply key remediation.');
            setConfirmOpen(false);
        } finally {
            setBusy(false);
        }
    };

    const rejectIssue = async () => {
        setBusy(true);
        setError('');
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-key-remediations/${rejecting.question_id}/reject`,
                { note: rejectionNote },
                { headers },
            );
            setNotice(`Question ${rejecting.question_id} audit issue marked rejected.`);
            setRejecting(null);
            setRejectionNote('');
            await loadQueue();
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'Failed to reject audit issue.');
        } finally {
            setBusy(false);
        }
    };

    const shown = queue.filter(item => {
        const state = issueState(item);
        if (filter === 'all') return true;
        if (filter === 'open') return state === 'open' || state === 'key_preview';
        return state === filter;
    });

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <FactCheckOutlinedIcon color="primary" />
                        <Typography variant="h3">Homework answer-key review</Typography>
                    </Stack>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                        Review the 27 confirmed audit findings. Nothing changes until an admin previews and explicitly applies a correction.
                    </Typography>
                </Box>
                <Button startIcon={<RefreshIcon />} onClick={loadQueue} disabled={loading}>Refresh</Button>
            </Stack>

            {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
            {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

            <Tabs value={filter} onChange={(_, value) => setFilter(value)} sx={{ mb: 2 }}>
                <Tab value="open" label="Open" />
                <Tab value="key_applied" label="Applied" />
                <Tab value="key_rejected" label="Rejected" />
                <Tab value="all" label="All" />
            </Tabs>

            {loading && <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>}
            {!loading && shown.length === 0 && (
                <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
                    <Typography color="text.secondary">No issues in this view.</Typography>
                </Paper>
            )}
            {!loading && shown.map(item => {
                const state = issueState(item);
                return (
                    <Paper key={item.id} variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography variant="h5">Question {item.question_id}</Typography>
                                    <Chip size="small" color={stateColor(state)} label={stateLabel(state)} />
                                    <Chip size="small" variant="outlined" label={`${item.affected_answer_count} answers`} />
                                    {item.kind === 'question_redesign' && <Chip size="small" color="warning" label="Redesign required" />}
                                </Stack>
                                <Typography sx={{ mt: 1 }}>{item.explanation}</Typography>
                                {item.question && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {textbookDisplay(item.question)}
                                        {' · '}page {item.question.page} · question {item.question.question_label}
                                    </Typography>
                                )}
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" fontWeight="bold">Current key</Typography>
                                        <Box component="pre" sx={{ m: 0, mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                            {formatValue(item.question?.answer)}
                                        </Box>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="caption" fontWeight="bold">Audit suggestion</Typography>
                                        <Box component="pre" sx={{ m: 0, mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                            {formatValue(item.proposed_answer)}
                                        </Box>
                                    </Box>
                                </Stack>
                                {item.latest_decision?.rejection_note && (
                                    <Alert severity="info" sx={{ mt: 2 }}>Rejection note: {item.latest_decision.rejection_note}</Alert>
                                )}
                            </Box>
                            <Stack direction={{ xs: 'row', md: 'column' }} spacing={1} justifyContent="flex-start">
                                <Button
                                    variant="contained"
                                    onClick={() => openEditor(item)}
                                    disabled={!item.question_found || item.apply_disabled}
                                >
                                    {state === 'key_preview' ? 'Edit / re-preview' : 'Review correction'}
                                </Button>
                                <Button color="inherit" onClick={() => setRejecting(item)} disabled={!item.question_found}>
                                    Reject finding
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                );
            })}

            <Dialog open={Boolean(editing)} onClose={busy ? undefined : () => setEditing(null)} fullWidth maxWidth="md">
                <DialogTitle>Propose key correction · Question {editing?.question_id}</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        One accepted key variant per line. Preview is read-only and must be confirmed separately before applying.
                    </Alert>
                    <TextField
                        fullWidth
                        multiline
                        minRows={5}
                        label="Proposed accepted answers"
                        value={proposedText}
                        onChange={event => setProposedText(event.target.value)}
                        disabled={busy}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
                    <Button variant="contained" onClick={createPreview} disabled={busy}>
                        {busy ? 'Creating preview…' : 'Preview all affected answers'}
                    </Button>
                </DialogActions>
            </Dialog>

            <PreviewDialog
                open={Boolean(preview)}
                preview={preview}
                applying={busy}
                onClose={() => setPreview(null)}
                onRequestApply={() => { setConfirmed(false); setConfirmOpen(true); }}
            />

            <Dialog open={confirmOpen} onClose={busy ? undefined : () => setConfirmOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm answer-key correction</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This writes the new key, creates key history, and changes {preview?.counts.upgrade || 0}
                        {' '}stored grade(s) from Incorrect to Correct. It will not apply any downgrade.
                    </Alert>
                    <FormControlLabel
                        control={<Checkbox checked={confirmed} onChange={event => setConfirmed(event.target.checked)} />}
                        label="I reviewed the complete change list and approve this correction."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={busy}>Cancel</Button>
                    <Button color="success" variant="contained" onClick={applyPreview} disabled={!confirmed || busy}>
                        {busy ? 'Applying…' : 'Apply key and upgrades'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(rejecting)} onClose={busy ? undefined : () => setRejecting(null)} fullWidth maxWidth="sm">
                <DialogTitle>Reject audit finding · Question {rejecting?.question_id}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={3}
                        label="Reason (optional)"
                        value={rejectionNote}
                        onChange={event => setRejectionNote(event.target.value)}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejecting(null)} disabled={busy}>Cancel</Button>
                    <Button color="warning" variant="contained" onClick={rejectIssue} disabled={busy}>Reject finding</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default KeyRemediationReview;
